<script lang="ts">
    import { onMount } from 'svelte';
    import { push, location } from 'svelte-spa-router';
    import BackButton from '../../BackButton.svelte';
    import CharityDonations from './CharityDonations.svelte';
    import CharityPayments from './CharityPayments.svelte';
    import employerCharityService, { 
        type Charity, 
        type CharityViewResponse,
        type DonationItem,
        type PaymentItem
    } from '../../../lib/api/employer/charity';
  
    // Get charity ID from props
    export let charityId: string = '';
  
    console.log('ViewCharity ID:', charityId);
  
    // Tab state
    let activeTab: 'donations' | 'payments' = 'donations';
  
    // Charity data state
    let charity: Charity | null = null;
    let charityView: CharityViewResponse | null = null;
    let loading = false;
    let error: string | null = null;

    // Filter state
    let filters = {
        donation_page: 1,
        donation_per_page: 20,
        donation_phrase: '',
        donation_status: '',
        payment_page: 1,
        payment_per_page: 100
    };
  
    // Function to load charity details and view data
    async function loadCharityDetails() {
        if (!charityId) {
            console.error('No charity ID provided');
            error = 'Invalid charity ID';
            return;
        }
  
        loading = true;
        error = null;
  
        try {
            console.log('Loading charity details for ID:', charityId);
            const [charityResponse, viewResponse] = await Promise.all([
                employerCharityService.getCharity(charityId),
                employerCharityService.getCharityView(charityId, filters)
            ]);

            if (charityResponse.success && viewResponse.success) {
                charity = charityResponse.data;
                charityView = viewResponse.data;
                console.log('Charity details loaded:', charity);
                console.log('Charity view data loaded:', charityView);
                console.log('Donations count:', charityView.donations.items.length);
                console.log('Payments count:', charityView.payments.items.length);
                console.log('First donation item:', charityView.donations.items[0]);
                console.log('First donation keys:', charityView.donations.items[0] ? Object.keys(charityView.donations.items[0]) : 'no items');
            } else {
                // Extract error message from API response
                const errorMessage = charityResponse.message || viewResponse.message || 'Failed to load charity details';
                error = errorMessage;
                console.error('API error:', errorMessage);
                console.error('Charity response:', charityResponse);
                console.error('View response:', viewResponse);
            }
        } catch (err: any) {
            console.error('Error loading charity:', err);
            console.error('Error response:', err?.response);
            console.error('Error response data:', err?.response?.data);
            
            // Extract error message from API response - handle multiple formats
            if (err?.response?.data) {
                const responseData = err.response.data;
                // Handle Laravel formatted response
                if (responseData.message) {
                    error = responseData.message;
                } else if (responseData.data?.message) {
                    error = responseData.data.message;
                } else if (typeof responseData === 'string') {
                    error = responseData;
                } else if (responseData.error) {
                    error = responseData.error;
                } else {
                    error = 'Failed to load charity details. Please try again.';
                }
            } else if (err?.message) {
                error = err.message;
            } else {
                error = 'Failed to load charity details. Please try again.';
            }
            
            // Log the full error for debugging
            console.error('Final error message:', error);
        } finally {
            loading = false;
        }
    }
  
    // Function to handle tab change
    function handleTabChange(tab: 'donations' | 'payments') {
        console.log('Changing tab to:', tab);
        activeTab = tab;
    }
  

    // Function to update filters and reload data
    async function updateFilters(newFilters: any) {
        filters = { ...filters, ...newFilters };
        await loadCharityDetails();
    }
  
    // Watch for changes in charityId prop
    let previousCharityId = '';
    $: {
        if (charityId && charityId !== previousCharityId) {
            previousCharityId = charityId;
            loadCharityDetails();
        }
    }
  
    onMount(() => {
        console.log('ViewCharity mounted with ID:', charityId);
        if (charityId) {
            loadCharityDetails();
        }
    });
</script>

<div class="charity-view-page">
    <div class="header">
        <BackButton text="Back" onClick={() => window.history.back()} />
    </div>
  
    {#if loading}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading charity details...</p>
        </div>
    {:else if error}
        <div class="error-container">
            <p class="error-message">{error}</p>
            <button class="retry-button" on:click={loadCharityDetails}>Try Again</button>
        </div>
    {:else if charity && charityView}
        <div class="charity-details">
            <div class="charity-header">
                <h1>{charity.name}</h1>
                <div class="charity-subinfo">
                    <span class="subinfo-item">ABN: {charity.abn || charity.charity_government_id}</span>
                </div>
                <div class="charity-category">
                    <span class="category-badge">{charity.category.name}</span>
                    {#if charity.verified === 1}
                        <span class="verified-badge">Verified</span>
                    {/if}
                </div>
            </div>
  
            <div class="charity-stats">
                <div class="stat-card">
                    <span class="stat-label">Contact Email</span>
                    <span class="stat-value">{charity.contact_email}</span>
                </div>
                <div class="stat-card">
                    <span class="stat-label">Contact Phone</span>
                    <span class="stat-value">{charity.contact_phone}</span>
                </div>
                <div class="stat-card">
                    <span class="stat-label">Location</span>
                    <span class="stat-value">{charity.head_office_location}</span>
                </div>
                {#if charity.website}
                    <div class="stat-card">
                        <span class="stat-label">Website</span>
                        <a href={charity.website} target="_blank" rel="noopener noreferrer" class="website-link">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                            Visit Website
                        </a>
                    </div>
                {/if}
            </div>
        </div>
  
        <div class="tabs">
            <button 
                class="tab-button" 
                class:active={activeTab === 'donations'} 
                on:click={() => handleTabChange('donations')}
            >
                Donations ({charityView.donations.paging.total})
            </button>
            <button 
                class="tab-button" 
                class:active={activeTab === 'payments'} 
                on:click={() => handleTabChange('payments')}
            >
                Payments ({charityView.payments.paging.total})
            </button>
        </div>
  
        <div class="tab-content">
            {#if activeTab === 'donations'}
                <CharityDonations 
                    donations={charityView.donations.items}
                    paging={charityView.donations.paging}
                    currentPhrase={filters.donation_phrase || ''}
                    currentStatus={filters.donation_status || ''}
                    on:updateFilters={(e) => updateFilters({
                        donation_page: e.detail.page || filters.donation_page,
                        donation_per_page: e.detail.per_page || filters.donation_per_page,
                        donation_phrase: e.detail.phrase !== undefined ? e.detail.phrase : filters.donation_phrase,
                        donation_status: e.detail.status !== undefined ? e.detail.status : filters.donation_status,
                        donation_date_from: e.detail.date_from,
                        donation_date_to: e.detail.date_to
                    })}
                />
            {:else if activeTab === 'payments'}
                <CharityPayments 
                    payments={charityView.payments.items}
                    paging={charityView.payments.paging}
                    on:updateFilters={(e) => updateFilters({
                        payment_page: e.detail.page,
                        payment_per_page: e.detail.per_page,
                        payment_phrase: e.detail.phrase,
                        payment_date_from: e.detail.date_from,
                        payment_date_to: e.detail.date_to,
                        payment_status: e.detail.status
                    })}
                />
            {/if}
        </div>
    {/if}
</div>
  
  <style>
    .charity-view-page {
      padding: 1.5rem;
      background-color: #f7fafc;
      min-height: 100vh;
    }
  
    .header {
      margin-bottom: 0;
    }
  
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      text-align: center;
    }
  
    .loading-spinner {
      width: 2rem;
      height: 2rem;
      border: 3px solid #f3f4f6;
      border-top: 3px solid #f0b429;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 1rem;
    }
  
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  
    .error-container {
      text-align: center;
      padding: 2rem;
    }
  
    .error-message {
      color: #dc2626;
      margin-bottom: 1rem;
    }
  
    .retry-button {
      padding: 0.5rem 1rem;
      background-color: #f0b429;
      color: white;
      border: none;
      border-radius: 0.375rem;
      cursor: pointer;
    }
  
    .charity-details {
      background: white;
      border-radius: 0.5rem;
      padding: 1.5rem;
      margin-bottom: 2rem;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    }
  
    .charity-header {
      margin-bottom: 1.5rem;
    }
  
    .charity-header h1 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #111827;
      margin: 0;
    }
  
    .charity-subinfo {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 0.25rem 0 0.75rem 0;
      color: #6B7280;
      font-size: 0.875rem;
    }
  
    .subinfo-item {
      color: #6B7280;
    }
  
    .separator {
      color: #D1D5DB;
    }
  
    .website-link {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      color: #4b5563;
      text-decoration: none;
      font-size: 0.875rem;
    }
  
    .website-link:hover {
      color: #1f2937;
    }
  
    .charity-stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
    }
  
    .stat-card {
      background: #f9fafb;
      padding: 1rem;
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
  
    .stat-label {
      font-size: 0.875rem;
      color: #6b7280;
    }
  
    .stat-value {
      font-size: 1.25rem;
      font-weight: 600;
      color: #111827;
    }
  
    .tabs {
      display: flex;
      gap: 1rem;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 1.5rem;
    }
  
    .tab-button {
      padding: 1rem 2rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: #6b7280;
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      cursor: pointer;
      transition: all 0.2s;
    }
  
    .tab-button:hover {
      color: #111827;
    }
  
    .tab-button.active {
      color: #f0b429;
      border-bottom-color: #f0b429;
    }
  
    .tab-content {
      background: white;
      border-radius: 0.5rem;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      min-height: 400px;
    }
  
    .charity-category {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-top: 0.5rem;
    }
  
    .category-badge {
      background-color: #E5E7EB;
      color: #374151;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.875rem;
      font-weight: 500;
    }
  
    .verified-badge {
      background-color: #DEF7EC;
      color: #03543F;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.875rem;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  
  </style> 